import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import { useCallback, useEffect, useMemo } from "react";
import type { CurrencyPrimitive } from "@/api/cashier";
import { Exchanger } from "@/components/exchanger/Exchanger";
import { Summarize } from "@/components/summarize/Summarize";
import { useAmountForm } from "@/hooks/deposit/useAmountForm";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useCurrencyConverter } from "@/hooks/deposit/useCurrencyConverter";
import { useMethod } from "@/hooks/deposit/useMethod";
import { EPaymentMethodGroupId } from "@/utils/enums";

export type EnterAmountSectionProps = {
  isLoading?: boolean;
  disabled?: boolean;
};

export const EnterAmountSection = ({
  isLoading,
  disabled,
}: EnterAmountSectionProps) => {
  const { selectedPaymentMethod, isPaymentGroupSelected } = useMethod();
  const {
    getPaymentMethodCurrencies,
    selectedAccountCurrency,
    selectedProcessingCurrency,
  } = useCurrencies();
  const { amountFormData, updateAmountFormData } = useAmountForm();
  const {
    handleCurrencyChange,
    handleAmountChange,
    refetchRate,
    cancelPendingConversion,
  } = useCurrencyConverter();

  const selectedPaymentMethodCurrencies = getPaymentMethodCurrencies(
    selectedPaymentMethod
  );

  const selectedCurrencyCode = amountFormData.selectedProcessingCurrency?.code;

  const tradingLimit = selectedCurrencyCode
    ? selectedPaymentMethod?.settings?.[selectedCurrencyCode]?.trading_currency
        ?.limit
    : undefined;

  const fee = selectedCurrencyCode
    ? selectedPaymentMethod?.settings?.[selectedCurrencyCode]?.fee
    : undefined;

  const isExchange = Boolean(
    selectedPaymentMethod &&
      selectedProcessingCurrency?.code !== selectedAccountCurrency?.code
  );

  const showExchangeRate = useMemo(() => {
    return (
      !selectedPaymentMethod?.exchange_rate_is_hidden &&
      !!amountFormData.rate &&
      selectedProcessingCurrency?.code !== selectedAccountCurrency?.code
    );
  }, [
    selectedPaymentMethod?.exchange_rate_is_hidden,
    amountFormData.rate,
    selectedProcessingCurrency?.code,
    selectedAccountCurrency?.code,
  ]);

  // cancelPendingConversion is called first to invalidate any in-flight prefill
  // conversion (e.g. from selecting a saved payment method) before the debounce
  // fires. Without it, a fast prefill API response can overwrite amountIsLoading
  // back to false with the old amount, re-enabling submission before the debounced
  // handleAmountChange has resolved the newly typed value.
  const onAmountInputChange = useCallback(() => {
    cancelPendingConversion();
    updateAmountFormData({ amountIsLoading: true, amountInputPending: true });
  }, [cancelPendingConversion, updateAmountFormData]);

  // On unmount (e.g. navigating away mid-debounce), clear the pending flags so
  // the form doesn't remain stuck-disabled when remounted.
  useEffect(() => {
    return () => {
      updateAmountFormData({
        amountIsLoading: false,
        amountInputPending: false,
      });
    };
  }, [updateAmountFormData]);
  const onProcessingAmountChangeStart = useCallback(
    async (amount: number) => handleAmountChange(amount, true),
    [handleAmountChange]
  );
  const onTradingAmountChangeStart = useCallback(
    async (amount: number) => handleAmountChange(amount, false),
    [handleAmountChange]
  );
  const onCurrencyChangeStart = useCallback(
    async (currency: CurrencyPrimitive) => {
      // todo show loading indicator / skeleton loader while fetching rate for new currency
      updateAmountFormData({
        tradingValue: 0,
        processingValue: 0,
        selectedProcessingCurrency: currency,
        externalProcessingCurrency: currency,
        externalTradingAmount: 0,
        externalProcessingAmount: 0,
        amountIsLoading: false,
        rate: undefined,
        validationError: null,
      });
      return handleCurrencyChange(currency);
    },
    [handleCurrencyChange, updateAmountFormData]
  );

  if (!selectedAccountCurrency || isLoading) {
    return (
      <div className="ps-flex ps-flex-col ps-gap-2">
        <SkeletonLoader variant="rectangular" height={16} width={80} />
        <SkeletonLoader variant="rectangular" height={16} width={80} />
      </div>
    );
  }

  const fallbackAmount = {
    amount: "0",
    code: selectedAccountCurrency.code,
    decimals: selectedAccountCurrency?.decimals ?? 2,
  };

  const selectedPaymentMethodLimits = {
    min: tradingLimit?.min
      ? { unlimited: false, value: tradingLimit.min }
      : { unlimited: true, value: fallbackAmount },
    max: tradingLimit?.max
      ? { unlimited: false, value: tradingLimit.max }
      : { unlimited: true, value: fallbackAmount },
  };

  return (
    <>
      <Exchanger
        processingCurrencies={selectedPaymentMethodCurrencies}
        accountCurrency={selectedAccountCurrency}
        onProcessingAmountChangeStart={onProcessingAmountChangeStart}
        onTradingAmountChangeStart={onTradingAmountChangeStart}
        onCurrencyChangeStart={onCurrencyChangeStart}
        onAmountInputChange={onAmountInputChange}
        showExchange={isExchange}
        externalTradingAmount={
          amountFormData.externalTradingAmount || undefined
        }
        externalProcessingAmount={
          amountFormData.externalProcessingAmount || undefined
        }
        externalSelectedCurrency={amountFormData.selectedProcessingCurrency}
        fee={fee}
        selectedPaymentMethodLimits={selectedPaymentMethodLimits}
        errorMessage={amountFormData.validationError?.message}
        disabled={Boolean(
          !selectedPaymentMethod || !selectedAccountCurrency || disabled
        )}
      />

      <Summarize
        data-testid="enter-amount-summarize"
        accountCurrency={selectedAccountCurrency}
        selectedProcessingCurrency={
          selectedProcessingCurrency?.code === selectedAccountCurrency.code
            ? selectedAccountCurrency
            : selectedProcessingCurrency
        }
        tradingValue={amountFormData.tradingValue}
        rate={amountFormData.rate}
        showRate={showExchangeRate}
        showTotal={!isPaymentGroupSelected(EPaymentMethodGroupId.BankWire)}
        refetchRate={refetchRate}
        fee={fee}
        enabledRefetch={!disabled && showExchangeRate}
        totalAmountCurrency={
          isExchange ? selectedAccountCurrency : selectedProcessingCurrency
        }
      />
    </>
  );
};
